import type { ResolvePublicScheduleResult } from "@/lib/course-schedule";

export const SELLOUT_ORIGIN = "https://www.cosmetic.college";

export const SELLOUT_FILTERS = [
  "Injectables",
  "Skin",
  "Laser",
  "Body",
  "Beauty",
  "Qualifications & teaching",
] as const;

export type SelloutFilter = (typeof SELLOUT_FILTERS)[number];

export type SelloutDateStatus = "open" | "full" | "unavailable";

export type SelloutBasket = {
  fields: {
    title: string;
    url: string | null;
    listingImage: { fields: { file: { url: string } } } | null;
  };
  sys: { id: string };
  event: {
    id: string | null;
    standardPrice: number | null;
    contentful_id: string | null;
    seat_price: number;
    elearning_course_id: string | null;
    package: boolean;
    events: [SelloutBasketEvent];
    wasPrice: number | null;
  };
  package: boolean;
  selectedEvent: SelloutBasketEvent;
};

export type SelloutBasketEvent = {
  id: string;
  start: string | null;
  end: string | null;
  date: string | null;
  seats: number | null;
  seat_price: number;
  price: number;
  courseSchedule: ResolvePublicScheduleResult | null;
  countStudentRegistrations: { aggregate: { count: number } } | null;
  detail: {
    id: string | null;
    title: string;
    price: number | null;
    contentful_id: string | null;
    elearning_course_id: string | null;
    package: boolean;
    wasPrice: number | null;
  };
};

export type SelloutDateRow = {
  id: string;
  title: string;
  start: string;
  dateKey: string;
  dateLabel: string;
  timeLabel: string;
  weekKey: string;
  weekLabel: string;
  seatsLeft: number | null;
  status: SelloutDateStatus;
  priceLabel: string | null;
  filter: SelloutFilter;
  courseUrl: string | null;
  basket: SelloutBasket;
};

export type SelloutBookingSpec = {
  key: string;
  date: string;
  test: (title: string) => boolean;
};

const titleHas = (pattern: RegExp) => (title: string) => pattern.test(title);

export function laserCareerCourseKey(row: Pick<SelloutDateRow, "title" | "dateKey">): string | null {
  if (!/^2026-\d{2}-\d{2}$/.test(row.dateKey)) return null;
  const date = new Date(`${row.dateKey}T00:00:00Z`);
  if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== row.dateKey) return null;
  const title = row.title.trim().toLowerCase();
  if (!/^vtct\b/.test(title) || !/\blaser hair removal\b/.test(title) || /tattoo|carbon/.test(title)) return null;
  return `laser-hair-removal-${row.dateKey}`;
}

export const SELLOUT_BOOKINGS: SelloutBookingSpec[] = [
  { key: "sclerotherapy-2026-10-22", date: "2026-10-22", test: titleHas(/sclerotherapy/) },
  { key: "vitamin-2026-10-22", date: "2026-10-22", test: titleHas(/vitamin/) },
  { key: "8-point-2026-10-27", date: "2026-10-27", test: titleHas(/8 point|eight point/) },
  { key: "lips-2026-10-28", date: "2026-10-28", test: titleHas(/\blips?\b/) },
  { key: "microneedling-2026-10-29", date: "2026-10-29", test: (title) => /microneedl/.test(title) && /level 4/.test(title) },
  { key: "prf-2026-11-03", date: "2026-11-03", test: titleHas(/\bprf\b/) },
  { key: "prp-2026-11-04", date: "2026-11-04", test: titleHas(/\bprp\b/) },
  { key: "aqualyx-2026-11-07", date: "2026-11-07", test: titleHas(/aqualyx/) },
  { key: "lemon-bottle-2026-11-07", date: "2026-11-07", test: titleHas(/lemon bottle/) },
  { key: "cannula-2026-11-10", date: "2026-11-10", test: titleHas(/cannula(?!.*(?:cheek|jaw))/) },
  { key: "plla-2026-11-10", date: "2026-11-10", test: titleHas(/\bplla\b|sculptra/) },
  { key: "cheeks-2026-11-11", date: "2026-11-11", test: titleHas(/cheek|jawline/) },
  { key: "hifu-2026-11-12", date: "2026-11-12", test: titleHas(/\bhifu\b|high frequency/) },
  { key: "boosters-2026-11-14", date: "2026-11-14", test: titleHas(/booster/) },
  { key: "polynucleotides-2026-11-14", date: "2026-11-14", test: titleHas(/polynucle/) },
  { key: "peels-2026-11-21", date: "2026-11-21", test: titleHas(/peel/) },
  { key: "lymphatic-2026-11-21", date: "2026-11-21", test: titleHas(/lymphatic/) },
  { key: "tear-trough-2026-11-24", date: "2026-11-24", test: titleHas(/tear trough/) },
  { key: "rhinoplasty-2026-11-24", date: "2026-11-24", test: titleHas(/rhino/) },
  { key: "education-2026-11-24", date: "2026-11-24", test: titleHas(/education/) },
  { key: "assessing-2026-11-25", date: "2026-11-25", test: titleHas(/assessing/) },
  { key: "wood-therapy-2026-11-27", date: "2026-11-27", test: titleHas(/wood/) },
  { key: "carbon-2026-11-27", date: "2026-11-27", test: titleHas(/carbon/) },
  { key: "iqa-2026-11-27", date: "2026-11-27", test: titleHas(/\biqa\b|quality assurance/) },
  { key: "bls-2026-11-28", date: "2026-11-28", test: titleHas(/basic life support|\bbls\b/) },
  { key: "foundation-2026-11-28", date: "2026-11-28", test: titleHas(/foundation/) },
  { key: "advanced-anti-wrinkle-2026-11-28", date: "2026-11-28", test: titleHas(/advanced/) },
  { key: "level-7-2026-11-30", date: "2026-11-30", test: titleHas(/level 7/) },
  { key: "rf-cavitation-2026-12-12", date: "2026-12-12", test: titleHas(/cavitation|\brf\b|radio frequency/) },
  { key: "boosters-2026-12-12", date: "2026-12-12", test: titleHas(/booster/) },
  { key: "lymphatic-2026-12-12", date: "2026-12-12", test: titleHas(/lymphatic/) },
  { key: "polynucleotides-2026-12-12", date: "2026-12-12", test: titleHas(/polynucle/) },
  { key: "brow-2026-11-20", date: "2026-11-20", test: titleHas(/brow/) },
  { key: "lash-2026-11-20", date: "2026-11-20", test: titleHas(/lash/) },
  { key: "tattoo-2026-12-14", date: "2026-12-14", test: titleHas(/tattoo/) },
];

export const SELLOUT_LINK_PATHS: Record<string, string> = {
  LINK_ALL_DATES: "/2026-dates/#all-dates",
  LINK_PRICE_PROMISE: "/2026-dates/#price-promise",
  LINK_CALLBACK: "/request-a-callback/",
  LINK_LASER_HUB: "/2026-dates/#laser",
  LINK_FILLER_PASS: "/2026-dates/#filler-pass",
  LINK_SKIN_TECH_DUO: "/2026-dates/#skin-tech-duo",
  LINK_LEVEL7_BLS: "/2026-dates/#level-7-bls",
  LINK_REGENERATIVE_PASS: "/2026-dates/#regenerative-pass",
  LINK_FULL_DAY_PASS: "/2026-dates/#full-day-pass",
  LINK_LASER_CAREER_BUNDLE: "/2026-dates/#laser-career-bundle",
  LINK_TRAINER_WEEK: "/2026-dates/#trainer-week",
  LINK_SCLERO_22OCT: "/2026-dates/book/sclerotherapy-2026-10-22/",
  LINK_VITAMIN_22OCT: "/2026-dates/book/vitamin-2026-10-22/",
  LINK_8POINT_27OCT: "/2026-dates/book/8-point-2026-10-27/",
  LINK_LIPS_28OCT: "/2026-dates/book/lips-2026-10-28/",
  LINK_NEEDLING_29OCT: "/2026-dates/book/microneedling-2026-10-29/",
  LINK_PRF_03NOV: "/2026-dates/book/prf-2026-11-03/",
  LINK_PRP_04NOV: "/2026-dates/book/prp-2026-11-04/",
  LINK_AQUALYX_07NOV: "/2026-dates/book/aqualyx-2026-11-07/",
  LINK_LEMON_07NOV: "/2026-dates/book/lemon-bottle-2026-11-07/",
  LINK_CANNULA_10NOV: "/2026-dates/book/cannula-2026-11-10/",
  LINK_PLLA_10NOV: "/2026-dates/book/plla-2026-11-10/",
  LINK_CHEEKS_11NOV: "/2026-dates/book/cheeks-2026-11-11/",
  LINK_HIFU_12NOV: "/2026-dates/book/hifu-2026-11-12/",
  LINK_BOOSTERS_14NOV: "/2026-dates/book/boosters-2026-11-14/",
  LINK_POLY_14NOV: "/2026-dates/book/polynucleotides-2026-11-14/",
  LINK_PEELS_21NOV: "/2026-dates/book/peels-2026-11-21/",
  LINK_LYMPH_21NOV: "/2026-dates/book/lymphatic-2026-11-21/",
  LINK_TEAR_24NOV: "/2026-dates/book/tear-trough-2026-11-24/",
  LINK_RHINO_24NOV: "/2026-dates/book/rhinoplasty-2026-11-24/",
  LINK_EDUCATION_24NOV: "/2026-dates/book/education-2026-11-24/",
  LINK_ASSESSING_25NOV: "/2026-dates/book/assessing-2026-11-25/",
  LINK_WOOD_27NOV: "/2026-dates/book/wood-therapy-2026-11-27/",
  LINK_CARBON_27NOV: "/2026-dates/book/carbon-2026-11-27/",
  LINK_IQA_27NOV: "/2026-dates/book/iqa-2026-11-27/",
  LINK_BLS_28NOV: "/2026-dates/book/bls-2026-11-28/",
  LINK_FOUNDATION_28NOV: "/2026-dates/book/foundation-2026-11-28/",
  LINK_ADVANCED_28NOV: "/2026-dates/book/advanced-anti-wrinkle-2026-11-28/",
  LINK_LEVEL7_30NOV: "/2026-dates/book/level-7-2026-11-30/",
  LINK_RF_12DEC: "/2026-dates/book/rf-cavitation-2026-12-12/",
  LINK_BOOSTERS_12DEC: "/2026-dates/book/boosters-2026-12-12/",
  LINK_LYMPH_12DEC: "/2026-dates/book/lymphatic-2026-12-12/",
  LINK_POLY_12DEC: "/2026-dates/book/polynucleotides-2026-12-12/",
  LINK_BROW_14DEC: "/2026-dates/book/brow-2026-11-20/",
  LINK_LASH_14DEC: "/2026-dates/book/lash-2026-11-20/",
  LINK_TATTOO_14DEC: "/2026-dates/book/tattoo-2026-12-14/",
};

export function classifySelloutCourse(title: string, category = ""): SelloutFilter {
  const text = `${title} ${category}`.toLowerCase();

  if (/laser|tattoo removal|\bipl\b|carbon facial|\bcarbon\b/.test(text)) {
    return "Laser";
  }
  if (/education|assessing|\biqa\b|quality assurance|level 7|basic life support|\bbls\b|access to aesthetic/.test(text)) {
    return "Qualifications & teaching";
  }
  if (/brow|lash|lamination/.test(text)) {
    return "Beauty";
  }
  if (/wood therapy|lymphatic|cavitation|aqualyx|lemon bottle|\brf\b|radio frequency|fat dissolv/.test(text)) {
    return "Body";
  }
  if (/peel|microneedl|\bhifu\b|dermaplan|plasma|mesotherapy|vitamin/.test(text)) {
    return "Skin";
  }
  if (/filler|\blips?\b|cannula|tear trough|rhino|8 point|eight point|anti-wrinkle|anti wrinkle|\bprp\b|\bprf\b|polynucle|\bplla\b|sculptra|skin booster|sclerotherapy|botox|toxin/.test(text)) {
    return "Injectables";
  }

  const normalisedCategory = category.toLowerCase();
  if (normalisedCategory === "injectables") return "Injectables";
  if (normalisedCategory === "beauty" || normalisedCategory === "permanent makeup") return "Beauty";
  if (normalisedCategory === "vtct") return "Qualifications & teaching";
  return "Skin";
}

/** Monday of the London calendar week for a YYYY-MM-DD civil date. */
export function londonWeekKey(dateKey: string): string {
  const [year, month, day] = dateKey.split("-").map(Number);
  const utc = new Date(Date.UTC(year, month - 1, day));
  const weekday = utc.getUTCDay();
  const daysFromMonday = weekday === 0 ? 6 : weekday - 1;
  utc.setUTCDate(utc.getUTCDate() - daysFromMonday);
  return utc.toISOString().slice(0, 10);
}

export function findSelloutDate(rows: SelloutDateRow[], key: string): SelloutDateRow | null {
  const spec = SELLOUT_BOOKINGS.find((booking) => booking.key === key);
  if (!spec) return null;
  return rows.find((row) => row.dateKey === spec.date && spec.test(row.title.toLowerCase())) ?? null;
}

export function addSelloutDate(
  session: { replaceCourses: (courses: unknown[]) => void },
  row: Pick<SelloutDateRow, "id" | "basket">,
) {
  let existing: Array<{ selectedEvent?: { id?: string } }> = [];
  try {
    const raw = sessionStorage.getItem("cc.courses");
    const parsed = raw ? JSON.parse(raw) : [];
    existing = Array.isArray(parsed) ? parsed : [];
  } catch {
    existing = [];
  }
  if (!existing.some((course) => course?.selectedEvent?.id === row.id)) {
    existing = [...existing, row.basket];
  }
  session.replaceCourses(existing);
}

export function withIncomingUtms(
  path: string,
  query: Record<string, string | string[] | undefined>,
): string {
  const params = new URLSearchParams();
  for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_content"]) {
    const value = query[key];
    if (typeof value === "string" && value) params.set(key, value);
  }
  const search = params.toString();
  if (!search) return path;
  const hashIndex = path.indexOf("#");
  const hash = hashIndex >= 0 ? path.slice(hashIndex) : "";
  const base = hashIndex >= 0 ? path.slice(0, hashIndex) : path;
  const joiner = base.includes("?") ? "&" : "?";
  return `${base}${joiner}${search}${hash}`;
}

export function selloutEmailUrl(path: string, email = "01"): string {
  const url = new URL(path, SELLOUT_ORIGIN);
  url.searchParams.set("utm_source", "emailoctopus");
  url.searchParams.set("utm_medium", "email");
  url.searchParams.set("utm_campaign", "2026-sellout");
  url.searchParams.set("utm_content", email);
  return url.toString();
}
